import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Can } from "@/casl.config";
import { formatPriceWithCurrency, smartParseFloat } from "@helpers/utils";
import { useAppSelector } from "@redux/hooks";
import {
    calculateLineMargin,
    formatMarginPercentage,
} from "@components/Dashboard/pages/Sales/Orders/contributionMargin";
import { AddProductFormSchemeTyping } from "../addProductDraftShared";

/**
 * Last purchase price (LPP) of the selected product and the margin of the unit price being typed,
 * rendered under the unit price field. Gated here so that the cost price can't leak through a
 * forgotten check at the mount point.
 */
export default function UnitPriceMarginHint() {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.orders.modals.addProduct.fields.unitPrice",
    });

    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency);

    const { control } = useFormContext<AddProductFormSchemeTyping>();
    const [product, rawUnitPrice, rawDiscount] = useWatch({
        control,
        name: ["product", "unit_price", "discount"],
    });

    if (!product) return null;

    // RHF keeps the raw input text while the user is typing, so the price may not be a number yet.
    // Until it is, there is no margin to show, only the LPP.
    const unitPrice = smartParseFloat(rawUnitPrice);
    const margin = Number.isFinite(unitPrice)
        ? calculateLineMargin({
              unitPrice: unitPrice as number,
              discount: smartParseFloat(rawDiscount),
              lastPurchasePrice: product.prices.purchase_price,
          })
        : undefined;

    const lpp = formatPriceWithCurrency(product.prices.purchase_price ?? 0, baseCurrency);

    return (
        <Can I="price.view" a="sale_order">
            <div className="mt-2 text-sm font-thin text-gray-600">
                {margin ? (
                    <Trans
                        t={t}
                        i18nKey="margin"
                        values={{
                            lpp,
                            margin: formatMarginPercentage(margin.marginPercentage),
                            perUnit: formatPriceWithCurrency(margin.marginPerUnit, baseCurrency),
                        }}
                        components={{ b: <b className="font-semibold" /> }}
                    />
                ) : (
                    <Trans
                        t={t}
                        i18nKey="lpp"
                        values={{ lpp }}
                        components={{ b: <b className="font-semibold" /> }}
                    />
                )}
            </div>
        </Can>
    );
}
